/**
 * Cross-layer collection cache — single source of truth for
 * `CollectionItem` rows (Groups / Collections / Stacks) across the
 * viewer.
 *
 * Backs `collectionStore.useList()`, `useListGroups`,
 * `useListAllForSlugLookup`, the multiselect action bar's Stack-parent
 * resolution, `<AppNav>` tab tinting, and most importantly the
 * homepage Stream's `mergeSameCollectionBlocks` step — which clusters
 * runs of blocks that share a non-primary `collectionId` into
 * "synthetic Stack" tiles. That grouping needs a fully populated
 * lookup map at first paint to avoid a visible "all unfurled then
 * gathered" flash on warm load.
 *
 * Per-surface filtering (Groups vs Collections vs Stacks) happens in
 * the consumer — `useList()` itself filters to layer-1 so the
 * "Collections" tab never accidentally surfaces a Group, and the
 * Groups switcher reads from its own dedicated query.
 *
 * Architectural shape (mirrors `block-collection.ts`):
 *
 *   - This collection is a pure key-value cache. Nothing evicts
 *     entries except an explicit `writeDelete` or the bootstrap
 *     hydrator's diff-sync delete (rows present in IDB but missing
 *     from the authoritative `/v1/bootstrap` payload — i.e. deleted
 *     on another device since last visit).
 *   - Two hydrators run sequentially on module load:
 *       1. `hydrateFromIdb` — replays the previous session's snapshot
 *          from IndexedDB (~20 ms). Cards / synthetic stacks paint
 *          from this on warm visits before the network resolves.
 *       2. `hydrateFromBootstrap` — awaits `getBootstrapPromise()`
 *          and writes the authoritative collection set in. Also
 *          deletes any IDB-hydrated row not present in the bootstrap
 *          payload so cross-device deletes propagate on next visit.
 *     The IDB-first, bootstrap-second ordering matters: if bootstrap
 *     won the race a slow IDB read could later land stale data on
 *     top of fresh network data. Sequencing keeps the trajectory a
 *     clean "stale paint → fresh reconcile".
 *   - Every other writer (`refreshCollections`, optimistic
 *     create/move/rename, soft-delete) writes through
 *     `utils.writeUpsert` / `writeDelete` / `writeBatch` — those
 *     callers are unchanged from the previous `queryCollectionOptions`
 *     shape because the manual-sync utils preserve the same API.
 *
 * Why manual sync (and not `queryCollectionOptions`):
 *
 *   The previous shape used `queryCollectionOptions({ queryFn:
 *   () => bootstrap.collections })`, which has no IDB hydration story
 *   — the queryFn must resolve before any data lands. Warm visits paid
 *   the full bootstrap latency (100-500 ms) just to populate the
 *   synthetic-stack lookup map, even though the previous session's
 *   snapshot was sitting in IndexedDB. Blocks (which DO have IDB
 *   persistence) painted in ~20 ms, then the homepage flashed when
 *   bootstrap finally landed and the grouping re-ran.
 *
 *   Manual sync lets us run two independent hydrators (IDB-fast,
 *   bootstrap-authoritative) and write through the same sync handles
 *   that the manual-mutation callers already use. The shape matches
 *   `block-collection.ts` so future maintainers find one pattern,
 *   not two.
 */
import { createCollection } from "@tanstack/react-db";
import { getBootstrapPromise } from "@/api/bootstrap-api";
import { debounce, readCollectionsSnapshot, writeCollectionsSnapshot, } from "./idb-persistence";
/**
 * How long to coalesce writes before flushing to IndexedDB. Matches
 * the block envelope cache's debounce — a `writeBatch` of N upserts
 * (e.g. bootstrap hydration writing 200 collections in one shot)
 * should flush ONCE after the burst settles, not 200 times.
 */
const IDB_PERSIST_DEBOUNCE_MS = 250;
let syncBegin = null;
let syncWrite = null;
let syncCommit = null;
let syncCollection = null;
let inBatch = false;
function writeUpsertImpl(collection) {
    if (syncBegin === null || syncWrite === null || syncCommit === null)
        return;
    const existing = syncCollection?.get(collection.id);
    const type = existing === undefined ? "insert" : "update";
    if (inBatch === false)
        syncBegin();
    syncWrite({ type, value: collection });
    if (inBatch === false)
        syncCommit();
}
function writeDeleteImpl(id) {
    if (syncBegin === null || syncWrite === null || syncCommit === null)
        return;
    const existing = syncCollection?.get(id);
    if (existing === undefined)
        return;
    if (inBatch === false)
        syncBegin();
    syncWrite({ type: "delete", value: existing });
    if (inBatch === false)
        syncCommit();
}
function writeBatchImpl(callback) {
    if (syncBegin === null || syncCommit === null) {
        callback();
        return;
    }
    if (inBatch === true) {
        // Nested batches flatten into the outer transaction.
        callback();
        return;
    }
    inBatch = true;
    syncBegin();
    try {
        callback();
    }
    finally {
        syncCommit();
        inBatch = false;
    }
}
// ---------------------------------------------------------------------------
// Collection
// ---------------------------------------------------------------------------
export const collectionCollection = createCollection({
    id: "cross-layer-collections",
    getKey: (item) => item.id,
    startSync: true,
    gcTime: 0,
    sync: {
        sync: (params) => {
            syncBegin = params.begin;
            syncWrite = params.write;
            syncCommit = params.commit;
            syncCollection = params.collection;
            params.markReady();
            return () => {
                syncBegin = null;
                syncWrite = null;
                syncCommit = null;
                syncCollection = null;
            };
        },
        getSyncMetadata: () => ({}),
    },
    utils: {
        writeUpsert: writeUpsertImpl,
        // Alias for callers that know the key is new (e.g. optimistic
        // placeholder writes for a freshly-created Stack). Same semantics
        // as upsert — the existing-key check inside `writeUpsertImpl`
        // picks "insert" vs "update" message type so a key collision
        // still updates rather than throwing.
        writeInsert: writeUpsertImpl,
        writeDelete: writeDeleteImpl,
        writeBatch: writeBatchImpl,
    },
});
// ---------------------------------------------------------------------------
// IDB persistence — write side
//
// `subscribeChanges` fires after every commit; we don't care which keys
// changed, just that the current state is the new truth. The debounced
// snapshot re-serializes the whole map in one IDB write.
// ---------------------------------------------------------------------------
const flushSnapshot = debounce(() => {
    void writeCollectionsSnapshot(Array.from(collectionCollection.values()));
}, IDB_PERSIST_DEBOUNCE_MS);
collectionCollection.subscribeChanges(() => {
    flushSnapshot();
});
// ---------------------------------------------------------------------------
// Hydration — fast warm path (IDB) + authoritative path (bootstrap)
//
// Both kick off at module load and race. IDB usually wins (read is a
// single key lookup, ~20 ms cold), so the synthetic-stack grouping
// has its lookup map populated before the homepage's first paint.
// Bootstrap follows shortly after with the authoritative state and
// reconciles any drift.
//
// `collectionsHydratedPromise` resolves once BOTH hydrators have
// settled (success or failure). Exported for any future caller that
// needs to coordinate against full hydration — analogous to
// `blockEnvelopesHydratedPromise` from `block-collection.ts`. No
// current consumer awaits it, but it's the public handle other
// modules would reach for if they needed a "collections cache
// settled" signal.
// ---------------------------------------------------------------------------
async function hydrateFromIdb() {
    try {
        const cached = await readCollectionsSnapshot();
        if (cached.length === 0)
            return;
        writeBatchImpl(() => {
            for (const collection of cached)
                writeUpsertImpl(collection);
        });
    }
    catch (e) {
        console.warn("[collection-collection] IDB hydrate failed:", e);
    }
}
async function hydrateFromBootstrap() {
    try {
        const payload = await getBootstrapPromise();
        // Bootstrap is the authoritative full collection set for this
        // user (every layer, every type — same shape the previous
        // `queryCollectionOptions` flow consumed). To preserve the
        // cross-device delete-propagation that the old `query-db-collection`
        // adapter gave us via diff-sync, we explicitly delete any row
        // currently in cache whose id isn't in the bootstrap payload.
        //
        // Optimistic placeholders aren't a concern here: they're created
        // on user action, which can only happen after first paint — i.e.
        // after this hydrator has already run. The diff-sync window is
        // strictly initial-load.
        const bootstrapIds = new Set();
        for (const c of payload.collections)
            bootstrapIds.add(c.id);
        const currentIds = Array.from(collectionCollection.keys());
        writeBatchImpl(() => {
            for (const collection of payload.collections)
                writeUpsertImpl(collection);
            for (const id of currentIds) {
                if (bootstrapIds.has(id) === false)
                    writeDeleteImpl(id);
            }
        });
    }
    catch (e) {
        console.warn("[collection-collection] bootstrap hydrate failed:", e);
    }
}
export const collectionsHydratedPromise = (async () => {
    // IDB first so warm visits paint from cache before bootstrap lands.
    // Bootstrap follows for the authoritative reconciliation. We don't
    // race them in parallel because if bootstrap won the race the
    // delayed IDB writes could clobber fresher network data — sequencing
    // gives a clean "stale paint → fresh reconcile" trajectory.
    await hydrateFromIdb();
    await hydrateFromBootstrap();
})();
